/**
 * Canonical Volume and Frequency badge calculation logic.
 * Shared by client UI, server notifications, analytics, and leaderboards.
 */

export type FrequencyBadgeColor = 'purple' | 'blue' | 'green' | 'yellow' | 'orange' | 'red';

export type FrequencyBadgeId = 'rhythm' | 'driven' | 'steady' | 'stray' | 'broken' | 'flatline';

export interface FrequencyBadge {
  id: FrequencyBadgeId;
  name: string;
  color: FrequencyBadgeColor;
  emoji: string;
  description: string;
  minMisses: number;
  maxMisses: number | null;
  /** Public asset path for designed badge artwork */
  imageSrc: string;
}

export type VolumeBadgeColor = 'red' | 'orange' | 'yellow' | 'green' | 'blue' | 'purple';

export interface VolumeBadge {
  name: string;
  emoji: string;
  description: string;
  minPercent: number;
  maxPercent: number | null;
  /** Public asset path for designed badge artwork */
  imageSrc: string;
  color: VolumeBadgeColor;
}

export const FREQUENCY_BADGES: FrequencyBadge[] = [
  {
    id: 'rhythm',
    name: 'Rhythm',
    color: 'purple',
    emoji: '🟣',
    description: '0 missed reminder intervals',
    minMisses: 0,
    maxMisses: 0,
    imageSrc: '/badges/frequency/rhythm.png',
  },
  {
    id: 'driven',
    name: 'Driven',
    color: 'blue',
    emoji: '🔵',
    description: '1–2 missed reminder intervals',
    minMisses: 1,
    maxMisses: 2,
    imageSrc: '/badges/frequency/driven.png',
  },
  {
    id: 'steady',
    name: 'Steady',
    color: 'green',
    emoji: '🟢',
    description: '3–4 missed reminder intervals',
    minMisses: 3,
    maxMisses: 4,
    imageSrc: '/badges/frequency/steady.png',
  },
  {
    id: 'stray',
    name: 'Stray',
    color: 'yellow',
    emoji: '🟡',
    description: '5–6 missed reminder intervals',
    minMisses: 5,
    maxMisses: 6,
    imageSrc: '/badges/frequency/stray.png',
  },
  {
    id: 'broken',
    name: 'Broken',
    color: 'orange',
    emoji: '🟠',
    description: '6–7 missed reminder intervals',
    minMisses: 6,
    maxMisses: 7,
    imageSrc: '/badges/frequency/broken.png',
  },
  {
    id: 'flatline',
    name: 'Flatline',
    color: 'red',
    emoji: '🔴',
    description: '7+ missed reminder intervals',
    minMisses: 7,
    maxMisses: null,
    imageSrc: '/badges/frequency/flatline.png',
  },
];

export const VOLUME_BADGES: VolumeBadge[] = [
  {
    name: 'H2nO?',
    emoji: '💧',
    description: '≤ 50% of expected volume',
    minPercent: 0,
    maxPercent: 50,
    imageSrc: '/badges/volume/h2no.png',
    color: 'red',
  },
  {
    name: 'H2Ooops',
    emoji: '😅',
    description: '50%–75% of expected volume',
    minPercent: 50,
    maxPercent: 75,
    imageSrc: '/badges/volume/h2ooops.png',
    color: 'orange',
  },
  {
    name: 'H2oh!',
    emoji: '👍',
    description: '75%–95% of expected volume',
    minPercent: 75,
    maxPercent: 95,
    imageSrc: '/badges/volume/h2oh.png',
    color: 'yellow',
  },
  {
    name: 'H2Ah!',
    emoji: '✨',
    description: '95%–100% of expected volume',
    minPercent: 95,
    maxPercent: 100,
    imageSrc: '/badges/volume/h2ah.png',
    color: 'green',
  },
  {
    name: 'H2wOw',
    emoji: '🔥',
    description: '100%–110% of expected volume',
    minPercent: 100,
    maxPercent: 110,
    imageSrc: '/badges/volume/h2wow.png',
    color: 'blue',
  },
  {
    name: 'H2gOat',
    emoji: '🐐',
    description: '> 110% of expected volume',
    minPercent: 110,
    maxPercent: null,
    imageSrc: '/badges/volume/h2goat.png',
    color: 'purple',
  },
];

export interface BadgeCalculationInput {
  /** Prorated daily goal in ml */
  dailyGoal: number;
  /** Total reminder intervals in the active day */
  totalReminderIntervals: number;
  /** Reminder intervals completed so far today */
  completedIntervals: number;
  /** Total water logged today in ml */
  totalWaterLogged: number;
  /** Total missed reminder intervals today (never decreases) */
  missCount: number;
}

export interface BadgeCalculationResult {
  volume: VolumeBadge;
  frequency: FrequencyBadge;
  volumePercentage: number;
  currentExpectedVolume: number;
  missCount: number;
  completedIntervals: number;
  totalReminderIntervals: number;
}

/**
 * Current Expected Volume =
 * (Daily Goal / Total Reminder Intervals) × Completed Intervals
 */
export function calculateCurrentExpectedVolume(
  dailyGoal: number,
  totalReminderIntervals: number,
  completedIntervals: number
): number {
  if (totalReminderIntervals <= 0 || completedIntervals <= 0) {
    return 0;
  }

  return (dailyGoal / totalReminderIntervals) * completedIntervals;
}

/**
 * Current Volume % =
 * (Total Water Logged / Current Expected Volume) × 100
 */
export function calculateCurrentVolumePercentage(
  totalWaterLogged: number,
  currentExpectedVolume: number
): number {
  if (currentExpectedVolume <= 0) {
    return 100;
  }

  return Math.round((totalWaterLogged / currentExpectedVolume) * 100 * 100) / 100;
}

/**
 * Map volume percentage to badge tiers.
 * Boundaries follow the spec: ≤50%, 50–75%, 75–95%, 95–100%, 100–110%, >110%.
 */
export function getVolumeBadge(percentage: number): VolumeBadge {
  if (percentage <= 50) {
    return VOLUME_BADGES[0];
  }
  if (percentage <= 75) {
    return VOLUME_BADGES[1];
  }
  if (percentage <= 95) {
    return VOLUME_BADGES[2];
  }
  if (percentage <= 100) {
    return VOLUME_BADGES[3];
  }
  if (percentage <= 110) {
    return VOLUME_BADGES[4];
  }
  return VOLUME_BADGES[5];
}

/**
 * Map total missed reminder intervals to frequency badge tiers.
 * Overlapping ranges in the spec are resolved in listed order (later rules override).
 */
export function getFrequencyBadge(missCount: number): FrequencyBadge {
  const safeMissCount = Math.max(0, Math.floor(missCount));

  let badge = FREQUENCY_BADGES[0];

  if (safeMissCount >= 1 && safeMissCount <= 2) {
    badge = FREQUENCY_BADGES[1];
  }
  if (safeMissCount >= 3 && safeMissCount <= 4) {
    badge = FREQUENCY_BADGES[2];
  }
  if (safeMissCount >= 5 && safeMissCount <= 6) {
    badge = FREQUENCY_BADGES[3];
  }
  if (safeMissCount >= 6 && safeMissCount <= 7) {
    badge = FREQUENCY_BADGES[4];
  }
  if (safeMissCount >= 7) {
    badge = FREQUENCY_BADGES[5];
  }

  return badge;
}

/** Calculate live Volume and Frequency badges from shared inputs. */
export function calculateBadges(input: BadgeCalculationInput): BadgeCalculationResult {
  const completedIntervals = Math.max(0, Math.floor(input.completedIntervals));
  const totalReminderIntervals = Math.max(0, Math.floor(input.totalReminderIntervals));
  let missCount = Math.max(0, Math.floor(input.missCount));

  // Rhythm means every elapsed interval was answered. Zero intake cannot be a
  // 0-miss day — those completed slots were missed, even if missCount was never stored.
  if (input.totalWaterLogged <= 0 && completedIntervals > 0) {
    missCount = Math.max(missCount, completedIntervals);
  }

  const currentExpectedVolume = calculateCurrentExpectedVolume(
    input.dailyGoal,
    totalReminderIntervals,
    completedIntervals
  );

  const volumePercentage = calculateCurrentVolumePercentage(
    input.totalWaterLogged,
    currentExpectedVolume
  );

  return {
    volume: getVolumeBadge(volumePercentage),
    frequency: getFrequencyBadge(missCount),
    volumePercentage,
    currentExpectedVolume,
    missCount,
    completedIntervals,
    totalReminderIntervals,
  };
}

export interface DailyBadgeSnapshot {
  volume: VolumeBadge;
  frequency: FrequencyBadge;
  volumePercentage: number;
  missCount: number;
  finalized: boolean;
}
